import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Modal, Select, Spin, notification } from 'antd'
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import LinkOutlined from '@ant-design/icons/LinkOutlined'
import { isNil } from 'lodash'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { selectors as companySelectors } from 'store/modules/companies'
import { useMyClientsQuery } from 'enrollment-platform/api/persons'
import { SimpleCustomerDto } from 'enrollment-platform/api/persons/dto/simple-customer.dto'
import { getAdminSettings } from 'enrollment-platform/utils/adminSettings'
import {
  useGetDeliveriesQuery,
  useMatchDeliveryMutation,
} from '../../api/invoice-delivery-api-client'
import { InvoiceDeliveryItemDto } from '../../api/dto/invoice-delivery.dto'

/* ── Styles ─────────────────────────────────────────────────────────────── */

const ProgressBar = styled.div<{ $pct: number }>`
  height: 4px;
  border-radius: 2px;
  background: #e5e7eb;
  overflow: hidden;
  margin-bottom: 16px;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ $pct }) => $pct}%;
    background: #16a34a;
    transition: width 0.3s ease;
  }
`

const ProgressLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
`

const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 52vh;
  overflow-y: auto;
  padding-right: 4px;
`

const RowItem = styled.div<{ $matched: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ $matched }) => ($matched ? '#bbf7d0' : '#e5e7eb')};
  background: ${({ $matched }) => ($matched ? '#f0fdf4' : '#ffffff')};
  transition:
    border-color 0.2s,
    background 0.2s;
`

const RowIcon = styled.div<{ $matched: boolean }>`
  font-size: 16px;
  flex-shrink: 0;
  color: ${({ $matched }) => ($matched ? '#16a34a' : '#6b7280')};
`

const RowMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`

const RowFilename = styled.div`
  max-width: 220px;
  font-size: 13px;
  color: #111827;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowCompanyName = styled.div`
  max-width: 220px;
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowMatchedLabel = styled.div`
  font-size: 12px;
  color: #15803d;
  font-weight: 500;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
`

const RowControls = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 0;
  color: #6b7280;
  font-size: 14px;
`

const EmptyStateIcon = styled.span`
  font-size: 32px;
  color: #16a34a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const SelectWrapper = styled.div`
  width: 200px;

  > .ant-select {
    width: 100%;
  }
`

/* ── Types ───────────────────────────────────────────────────────────────── */

interface MatchedEntry {
  clientName: string
}

interface ResolveUnmatchedModalProps {
  open: boolean
  billingPeriodStart: string
  onClose: () => void
}

/* ── Single row component ────────────────────────────────────────────────── */

interface UnmatchedRowProps {
  delivery: InvoiceDeliveryItemDto
  clients: SimpleCustomerDto[]
  clientsLoading: boolean
  matchedEntry: MatchedEntry | undefined
  onMatched: (delivery: InvoiceDeliveryItemDto, clientName: string) => void
}

const UnmatchedRow: React.FC<UnmatchedRowProps> = React.memo(
  ({ delivery, clients, clientsLoading, matchedEntry, onMatched }) => {
    const [selectedClientId, setSelectedClientId] = useState<
      string | undefined
    >(undefined)

    const matchMutation = useMatchDeliveryMutation({
      onSuccess: (updated) => {
        const client = clients.find((c) => c.id === selectedClientId)
        onMatched(updated, client?.name ?? '')
        setSelectedClientId(undefined)
      },
      onError: (error: unknown) => {
        const data = (
          error as {
            response?: { data?: { message?: string; error?: string } }
          }
        )?.response?.data
        const description =
          typeof data?.message === 'string'
            ? data.message
            : (data?.error ?? 'An unexpected error occurred')
        notification.error({ message: 'Failed to match company', description })
      },
    })

    const handleMatch = () => {
      if (!selectedClientId) return
      const client = clients.find((c) => c.id === selectedClientId)
      if (!client) return
      matchMutation.mutate({
        id: delivery.id,
        body: { clientId: client.id, clientName: client.name },
      })
    }

    const isMatched = !isNil(matchedEntry)
    const currentCompany = matchedEntry?.clientName ?? delivery.clientName

    return (
      <RowItem $matched={isMatched}>
        <RowIcon $matched={isMatched}>
          {isMatched ? <CheckCircleOutlined /> : <FileTextOutlined />}
        </RowIcon>

        <RowMeta>
          <RowFilename title={delivery.fileName}>
            {delivery.fileName}
          </RowFilename>
          {currentCompany && (
            <RowCompanyName title={currentCompany}>
              {currentCompany}
            </RowCompanyName>
          )}
        </RowMeta>

        {isMatched ? (
          <RowMatchedLabel>
            <CheckCircleOutlined />
            {matchedEntry.clientName}
          </RowMatchedLabel>
        ) : (
          <RowControls>
            <SelectWrapper>
              <Select
                showSearch
                placeholder={clientsLoading ? 'Loading…' : 'Search company…'}
                loading={clientsLoading}
                disabled={clientsLoading || matchMutation.isPending}
                value={selectedClientId}
                onChange={setSelectedClientId}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                size="small"
              >
                {clients.map((c) => (
                  <Select.Option key={c.id} value={c.id}>
                    {c.name}
                  </Select.Option>
                ))}
              </Select>
            </SelectWrapper>
            <Button
              type="primary"
              size="small"
              htmlType="button"
              disabled={isNil(selectedClientId)}
              loading={matchMutation.isPending}
              onClick={handleMatch}
              icon={<LinkOutlined />}
            >
              Match
            </Button>
          </RowControls>
        )}
      </RowItem>
    )
  }
)
UnmatchedRow.displayName = 'UnmatchedRow'

/* ── Modal ───────────────────────────────────────────────────────────────── */

const ResolveUnmatchedModal: React.FC<ResolveUnmatchedModalProps> = ({
  open,
  billingPeriodStart,
  onClose,
}) => {
  const { data: rawClients = [], isLoading: clientsLoading } =
    useMyClientsQuery()
  const companiesFromStore = useSelector(
    companySelectors.companyCollectionSelector
  )
  const { showTestData } = getAdminSettings().finance
  const testClientIds = useMemo(
    () =>
      new Set(
        (companiesFromStore as unknown as Array<Record<string, unknown>>)
          .filter((c) => c.isTestClient === true && typeof c.id === 'string')
          .map((c) => c.id as string)
      ),
    [companiesFromStore]
  )
  const clients = showTestData
    ? rawClients
    : rawClients.filter((c) => !testClientIds.has(c.id))
  const [matchedMap, setMatchedMap] = useState<Map<string, MatchedEntry>>(
    () => new Map()
  )
  /*
   * Snapshot the list the first time data arrives. We use this as the stable
   * source of truth for the row list and count so that background refetches
   * (triggered by the match mutation invalidating the query key) don't shrink
   * the denominator mid-session and falsely trigger "all resolved".
   */
  const [snapshotDeliveries, setSnapshotDeliveries] = useState<
    InvoiceDeliveryItemDto[]
  >([])

  // Reset session state when the modal opens so a fresh open doesn't inherit
  // matched/resolved state from the previous session.
  useEffect(() => {
    if (!open) return
    setMatchedMap(new Map())
    setSnapshotDeliveries([])
  }, [open])

  const deliveriesQuery = useGetDeliveriesQuery(
    { billingPeriodStart, isMatched: false },
    { enabled: open }
  )

  // Capture the list exactly once on first successful load
  useEffect(() => {
    if (
      deliveriesQuery.isSuccess &&
      snapshotDeliveries.length === 0 &&
      (deliveriesQuery.data?.deliveries?.length ?? 0) > 0
    ) {
      setSnapshotDeliveries(deliveriesQuery.data?.deliveries ?? [])
    }
  }, [
    deliveriesQuery.isSuccess,
    deliveriesQuery.data,
    snapshotDeliveries.length,
  ])

  // Pending rows first (in original order), resolved rows at the bottom.
  // Memoized so the array identity is stable across renders that don't
  // affect ordering — keeps React's reconciliation cheap.
  const orderedDeliveries = useMemo(() => {
    const pending: InvoiceDeliveryItemDto[] = []
    const resolved: InvoiceDeliveryItemDto[] = []
    for (const d of snapshotDeliveries) {
      if (matchedMap.has(d.id)) resolved.push(d)
      else pending.push(d)
    }
    return [...pending, ...resolved]
  }, [snapshotDeliveries, matchedMap])

  const totalCount = snapshotDeliveries.length
  const resolvedCount = matchedMap.size
  const pct =
    totalCount > 0 ? Math.round((resolvedCount / totalCount) * 100) : 0
  const allResolved = totalCount > 0 && resolvedCount >= totalCount

  const handleMatched = useCallback(
    (delivery: InvoiceDeliveryItemDto, clientName: string) => {
      setMatchedMap((prev) => {
        const next = new Map(prev)
        next.set(delivery.id, { clientName })
        return next
      })
    },
    []
  )

  return (
    <Modal
      open={open}
      title={`Resolve unmatched invoices${totalCount > 0 ? ` (${totalCount})` : ''}`}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      width={640}
      destroyOnClose
    >
      {deliveriesQuery.isLoading ? (
        <EmptyState>
          <Spin />
          <span>Loading unmatched invoices…</span>
        </EmptyState>
      ) : totalCount === 0 ? (
        <EmptyState>
          <EmptyStateIcon>
            <CheckCircleOutlined />
          </EmptyStateIcon>
          <span>No unmatched invoices — you&apos;re all set.</span>
        </EmptyState>
      ) : allResolved ? (
        <EmptyState>
          <EmptyStateIcon>
            <CheckCircleOutlined />
          </EmptyStateIcon>
          <span>
            All {totalCount} invoice{totalCount === 1 ? '' : 's'} matched —
            you&apos;re good to go.
          </span>
        </EmptyState>
      ) : (
        <>
          <ProgressLabel>
            {resolvedCount} of {totalCount} resolved
          </ProgressLabel>
          <ProgressBar $pct={pct} />

          <RowList>
            {orderedDeliveries.map((delivery) => (
              <UnmatchedRow
                key={delivery.id}
                delivery={delivery}
                clients={clients}
                clientsLoading={clientsLoading}
                matchedEntry={matchedMap.get(delivery.id)}
                onMatched={handleMatched}
              />
            ))}
          </RowList>
        </>
      )}
    </Modal>
  )
}

export { ResolveUnmatchedModal }
